import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { FolderOpen } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { upsertCategorySchema } from '@nokowazzi/types';
import type { UpsertCategoryInput } from '@nokowazzi/types';
import { Badge, Button, EmptyState, Input, Spinner, Switch, toast } from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { Can } from '../features/admin/Can';
import { adminCategoriesQuery } from '../features/admin/queries';
import type { AdminCategoryRow } from '../features/admin/queries';
import { ApiError, apiFetch } from '../lib/api';
import { jsonLocaleText } from '../lib/json-i18n';
import { zodFieldErrors } from '../lib/zod-errors';

export const Route = createFileRoute('/_admin/categories')({
  component: CategoriesScreen,
});

interface CategoryFormState {
  /** null = 신규 등록 모드 */
  id: string | null;
  slug: string;
  nameKo: string;
  nameJa: string;
  sortOrder: string;
  isActive: boolean;
}

const EMPTY_FORM: CategoryFormState = {
  id: null,
  slug: '',
  nameKo: '',
  nameJa: '',
  sortOrder: '0',
  isActive: true,
};

function toFormState(category: AdminCategoryRow): CategoryFormState {
  return {
    id: category.id,
    slug: category.slug,
    nameKo: jsonLocaleText(category.name_i18n, 'ko'),
    nameJa: jsonLocaleText(category.name_i18n, 'ja'),
    sortOrder: String(category.sort_order),
    isActive: category.is_active,
  };
}

function CategoriesScreen() {
  const queryClient = useQueryClient();
  const categoriesQuery = useQuery(adminCategoriesQuery());
  const [form, setForm] = useState<CategoryFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const saveMutation = useMutation({
    mutationFn: (vars: { id: string | null; input: UpsertCategoryInput }) =>
      vars.id != null
        ? apiFetch<unknown>(`/v1/admin/categories/${vars.id}`, { method: 'PUT', json: vars.input })
        : apiFetch<unknown>('/v1/admin/categories', { method: 'POST', json: vars.input }),
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
      toast.show({
        type: 'success',
        message: vars.id != null ? '카테고리가 수정되었습니다' : '카테고리가 등록되었습니다',
      });
      setForm(EMPTY_FORM);
      setErrors({});
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '저장에 실패했습니다',
      });
    },
  });

  const set = <K extends keyof CategoryFormState>(key: K, value: CategoryFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const raw = {
      slug: form.slug.trim(),
      nameI18n: { ko: form.nameKo.trim(), ja: form.nameJa.trim() },
      sortOrder: form.sortOrder.trim() === '' ? 0 : Number(form.sortOrder),
      isActive: form.isActive,
    };
    const parsed = upsertCategorySchema.safeParse(raw);
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    saveMutation.mutate({ id: form.id, input: parsed.data });
  };

  const categories = categoriesQuery.data ?? [];

  return (
    <>
      <PageHeader title="카테고리" />

      <Can perm="categories:write">
        <form
          onSubmit={handleSubmit}
          noValidate
          className="mb-6 rounded-lg border border-line bg-surface p-6"
        >
          <h2 className="mb-4 text-title3 text-fg">
            {form.id != null ? '카테고리 수정' : '카테고리 등록'}
          </h2>
          <div className="grid grid-cols-4 items-start gap-x-4 gap-y-4">
            <Input
              label="슬러그"
              value={form.slug}
              onChange={(e) => set('slug', e.target.value)}
              placeholder="fashion"
              maxLength={50}
              error={errors['slug']}
              helperText="소문자·숫자·하이픈"
            />
            <Input
              label="이름 (한국어)"
              value={form.nameKo}
              onChange={(e) => set('nameKo', e.target.value)}
              maxLength={50}
              error={errors['nameI18n.ko']}
            />
            <Input
              label="이름 (일본어)"
              value={form.nameJa}
              onChange={(e) => set('nameJa', e.target.value)}
              maxLength={50}
              error={errors['nameI18n.ja']}
            />
            <Input
              label="정렬"
              type="number"
              inputMode="numeric"
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
              error={errors['sortOrder']}
            />
          </div>
          <div className="mt-4 flex items-center justify-between">
            <Switch
              label="활성"
              checked={form.isActive}
              onCheckedChange={(checked) => set('isActive', checked)}
            />
            <div className="flex items-center gap-2">
              {form.id != null && (
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => {
                    setForm(EMPTY_FORM);
                    setErrors({});
                  }}
                >
                  새로 만들기
                </Button>
              )}
              <Button type="submit" variant="primary" size="sm" loading={saveMutation.isPending}>
                저장
              </Button>
            </div>
          </div>
        </form>
      </Can>

      {categoriesQuery.isPending ? (
        <div className="flex min-h-52 items-center justify-center">
          <Spinner label="카테고리 목록 불러오는 중" />
        </div>
      ) : categoriesQuery.isError ? (
        <EmptyState
          title="카테고리 목록을 불러오지 못했습니다"
          description="잠시 후 다시 시도해 주세요"
          action={
            <Button variant="tertiary" size="sm" onClick={() => void categoriesQuery.refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : categories.length === 0 ? (
        <EmptyState
          icon={<FolderOpen size={40} strokeWidth={1.5} />}
          title="카테고리가 없습니다"
          description="위 폼으로 첫 카테고리를 등록해 보세요"
        />
      ) : (
        <Table>
          <Thead>
            <tr>
              <Th className="w-40">슬러그</Th>
              <Th>이름 (한국어)</Th>
              <Th>이름 (일본어)</Th>
              <Th className="w-16 text-right">정렬</Th>
              <Th className="w-20">상태</Th>
              <Th className="w-20 text-right">
                <span className="sr-only">액션</span>
              </Th>
            </tr>
          </Thead>
          <tbody>
            {categories.map((category) => (
              <Tr key={category.id}>
                <Td className="font-mono text-fg-secondary">{category.slug}</Td>
                <Td className="font-medium">{jsonLocaleText(category.name_i18n, 'ko')}</Td>
                <Td>{jsonLocaleText(category.name_i18n, 'ja')}</Td>
                <Td className="text-right tabular-nums">{category.sort_order}</Td>
                <Td>
                  {category.is_active ? (
                    <Badge tone="success">활성</Badge>
                  ) : (
                    <Badge tone="neutral">비활성</Badge>
                  )}
                </Td>
                <Td className="text-right">
                  <Can perm="categories:write">
                    <button
                      type="button"
                      onClick={() => {
                        setForm(toFormState(category));
                        setErrors({});
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="inline-flex h-8 items-center rounded-md bg-subtle px-2.5 font-medium text-fg transition-colors hover:bg-neutral-200"
                    >
                      수정
                    </button>
                  </Can>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}
